import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  Plus, Loader2, FolderTree, Layers, X, AlertTriangle, CheckCircle2, Save,
} from 'lucide-react';
import {
  useIndustries, useProjects, useDeliverables,
  useCreateProject, useCreateDeliverable,
} from '../../hooks/api/catalog';
import { useAssignProjects } from '../../hooks/api/admin';
import { refreshUserProfile } from '../../lib/auth-api';

/**
 * ProjectCatalogManager — unified picker for industries → projects →
 * deliverables. Reusable in two contexts:
 *
 *   - **Onboarding flow** (`mode="select"`): the caller passes
 *     `selectedProjectIds` + `onSelectionChange` to capture which projects
 *     should be enabled when the new client is created. Adding a brand-new
 *     project / deliverable inline auto-marks it as selected.
 *
 *   - **Existing client** (`mode="manage"`): pass `clientId` and
 *     `enabledProjectIds`. The "Save Enabled Projects" button calls
 *     `/admin/clients/{id}/projects` so newly-checked projects become
 *     ClientProject rows. Adding a new project/deliverable in this mode
 *     stages the change and offers the same Save action.
 *
 * Codes auto-generate: PRJ_NNN per industry, DEL_NNN per project. The
 * user can override by typing an explicit code in the inline form.
 */
export function ProjectCatalogManager({
  mode = 'select',
  clientId,                   // required for `mode==="manage"`
  clientCode,                 // shown in the S3-prefix preview tag
  enabledProjectIds = [],     // pre-checked project ids
  selectedProjectIds,
  onSelectionChange,
}) {
  const isManage = mode === 'manage';
  const { data: industries, isLoading: loadingInd } = useIndustries();

  // --- selection state -----------------------------------------------------
  // For `select` mode, selection is fully controlled by the parent.
  // For `manage` mode, we track local edits until the user hits Save.
  const [localSel, setLocalSel] = useState(() => new Set(enabledProjectIds || []));
  useEffect(() => {
    if (isManage) setLocalSel(new Set(enabledProjectIds || []));
  }, [isManage, enabledProjectIds]);

  const selection = isManage ? localSel : (selectedProjectIds || new Set());
  const setSelection = (next) => {
    if (isManage) {
      setLocalSel(new Set(next));
    } else if (typeof onSelectionChange === 'function') {
      onSelectionChange(new Set(next));
    }
  };
  const toggleProject = (id) => {
    const next = new Set(selection);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelection(next);
  };

  // --- expanded industry rows ---------------------------------------------
  const [expandedIndustries, setExpandedIndustries] = useState(() => new Set());
  const toggleIndustry = (id) => setExpandedIndustries((prev) => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  // Auto-expand industries that already contain a selected/enabled project.
  useEffect(() => {
    if (!industries) return;
    setExpandedIndustries((prev) => {
      const next = new Set(prev);
      // Best-effort heuristic: open every industry that has at least one
      // selected project. We don't know project→industry mapping until the
      // sub-query loads, so this widens once those queries settle.
      return next;
    });
  }, [industries]);

  // --- Save action (manage mode) ------------------------------------------
  const assign = useAssignProjects();
  const dirty = useMemo(() => {
    if (!isManage) return false;
    const a = new Set(enabledProjectIds || []);
    if (a.size !== localSel.size) return true;
    for (const x of a) if (!localSel.has(x)) return true;
    return false;
  }, [isManage, enabledProjectIds, localSel]);

  const handleSave = async () => {
    if (!isManage || !clientId) return;
    await assign.mutateAsync({
      id: clientId,
      project_ids: Array.from(localSel),
    });
  };

  if (loadingInd) {
    return (
      <div className="flex items-center gap-2 py-12 justify-center text-sm text-muted-foreground">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading industries…
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <FolderTree className="w-4 h-4" />
          {isManage
            ? <>Select which projects this client can access. New projects/deliverables created here also enable for this client.</>
            : <>Pick from existing industries → projects → deliverables, or click <strong>+ Add</strong> to create new ones inline.</>}
        </div>
        {isManage && dirty && (
          <Button onClick={handleSave} disabled={assign.isPending}>
            {assign.isPending
              ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              : <Save className="w-4 h-4 mr-2" />}
            Save enabled projects
          </Button>
        )}
      </div>

      {(industries || []).map((ind) => (
        <IndustryBlock
          key={ind.id}
          industry={ind}
          clientCode={clientCode}
          isExpanded={expandedIndustries.has(ind.id)}
          onToggleExpanded={() => toggleIndustry(ind.id)}
          selection={selection}
          onToggleProject={toggleProject}
          isManage={isManage}
          clientId={clientId}
          onProjectCreated={async (newProjectId) => {
            // In manage mode, immediately enable the new project for the
            // client so the user doesn't have to click Save afterwards.
            if (isManage && clientId) {
              const next = new Set(localSel);
              next.add(newProjectId);
              setLocalSel(next);
              try {
                await assign.mutateAsync({
                  id: clientId,
                  project_ids: Array.from(next),
                });
              } catch { /* error toast handled by parent caller */ }
            } else {
              // Select-mode: just check it.
              const next = new Set(selection);
              next.add(newProjectId);
              setSelection(next);
            }
            // Either way: refresh the auth store's navigation tree so
            // Annotator Upload + sidebar see the new project.
            try { await refreshUserProfile(); } catch { /* best-effort */ }
          }}
          onDeliverableCreated={async () => {
            try { await refreshUserProfile(); } catch { /* best-effort */ }
          }}
        />
      ))}
      {(!industries || industries.length === 0) && (
        <div className="text-center text-sm text-muted-foreground py-8">
          No industries in the master catalog yet.
        </div>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// One industry row + (when expanded) its projects + each project's deliverables.
// -----------------------------------------------------------------------------
function IndustryBlock({
  industry, clientCode, isExpanded, onToggleExpanded,
  selection, onToggleProject,
  isManage, clientId, onProjectCreated, onDeliverableCreated,
}) {
  const { data: projects, isLoading } = useProjects(isExpanded ? industry.id : null);
  const createProject = useCreateProject();

  const [showAddProject, setShowAddProject] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const [newProjectCode, setNewProjectCode] = useState('');
  const [error, setError] = useState('');

  const enabledHere = useMemo(() => {
    return (projects || []).filter((p) => selection.has(p.id)).length;
  }, [projects, selection]);

  const submitNewProject = async () => {
    setError('');
    if (!newProjectName.trim()) {
      setError('Name is required');
      return;
    }
    try {
      const created = await createProject.mutateAsync({
        industry_id: industry.id,
        name: newProjectName.trim(),
        code: newProjectCode.trim() || undefined,
      });
      setNewProjectName('');
      setNewProjectCode('');
      setShowAddProject(false);
      // Parent decides what to do — in manage mode it auto-enables the new
      // project for the client; in select mode it just ticks the box. Both
      // paths refresh the auth store's navigation so Annotator Upload sees it.
      if (typeof onProjectCreated === 'function') {
        await onProjectCreated(created.id);
      }
    } catch (e) {
      setError(e.message || 'Could not create project');
    }
  };

  return (
    <Card className="border-0 bg-card overflow-hidden">
      <CardHeader className="cursor-pointer select-none" onClick={onToggleExpanded}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Layers className={`w-5 h-5 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
            <CardTitle className="text-base">{industry.name}</CardTitle>
            <Badge variant="outline" className="text-[10px] font-mono">{industry.code}</Badge>
          </div>
          <div className="flex items-center gap-2">
            {enabledHere > 0 && (
              <Badge className="bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 text-[11px]">
                {enabledHere} selected
              </Badge>
            )}
            <Badge variant="outline" className="text-[10px]">
              {(projects || []).length} project{(projects || []).length === 1 ? '' : 's'}
            </Badge>
          </div>
        </div>
      </CardHeader>
      {isExpanded && (
        <CardContent className="pt-0 space-y-3">
          {isLoading && (
            <div className="flex items-center gap-2 py-3 text-sm text-muted-foreground">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading projects…
            </div>
          )}

          {(projects || []).map((p) => (
            <ProjectRow
              key={p.id}
              project={p}
              industryCode={industry.code}
              clientCode={clientCode}
              selected={selection.has(p.id)}
              onToggle={() => onToggleProject(p.id)}
              onDeliverableCreated={onDeliverableCreated}
            />
          ))}

          {/* Inline new-project form */}
          {showAddProject ? (
            <div className="rounded-lg border border-dashed border-muted-foreground/30 p-3 space-y-2 bg-background/40">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                <div className="md:col-span-2 space-y-1">
                  <Label className="text-xs">Project name</Label>
                  <Input
                    placeholder="e.g. Lake Encroachment"
                    value={newProjectName}
                    onChange={(e) => setNewProjectName(e.target.value)}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Code (optional — auto-suggests next PRJ_NNN)</Label>
                  <Input
                    placeholder="PRJ_002"
                    value={newProjectCode}
                    onChange={(e) => setNewProjectCode(e.target.value.toUpperCase())}
                    className="font-mono text-sm"
                  />
                </div>
              </div>
              {error && (
                <div className="text-xs text-destructive flex items-center gap-1.5">
                  <AlertTriangle className="w-3.5 h-3.5" /> {error}
                </div>
              )}
              <div className="flex justify-end gap-2 pt-1">
                <Button type="button" size="sm" variant="ghost"
                        onClick={() => { setShowAddProject(false); setError(''); }}>
                  Cancel
                </Button>
                <Button type="button" size="sm" onClick={submitNewProject}
                        disabled={createProject.isPending}>
                  {createProject.isPending
                    ? <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" />
                    : <Plus className="w-3.5 h-3.5 mr-2" />}
                  Create project
                </Button>
              </div>
            </div>
          ) : (
            <Button variant="outline" size="sm" type="button"
                    onClick={() => setShowAddProject(true)}>
              <Plus className="w-3.5 h-3.5 mr-2" /> Add project to {industry.code}
            </Button>
          )}
        </CardContent>
      )}
    </Card>
  );
}

// -----------------------------------------------------------------------------
// One project row + its deliverables (collapsible).
// -----------------------------------------------------------------------------
function ProjectRow({
  project, industryCode, clientCode, selected, onToggle, onDeliverableCreated,
}) {
  const [open, setOpen] = useState(false);
  const { data: dels, isLoading } = useDeliverables(open ? project.id : null);
  const createDeliverable = useCreateDeliverable();

  const [showAddDel, setShowAddDel] = useState(false);
  const [newName, setNewName] = useState('');
  const [newCode, setNewCode] = useState('');
  const [delError, setDelError] = useState('');

  const submitNewDeliverable = async () => {
    setDelError('');
    if (!newName.trim()) {
      setDelError('Name is required');
      return;
    }
    try {
      await createDeliverable.mutateAsync({
        project_id: project.id,
        name: newName.trim(),
        code: newCode.trim() || undefined,
      });
      setNewName('');
      setNewCode('');
      setShowAddDel(false);
      // Tell the parent so the auth store's navigation tree refreshes.
      if (typeof onDeliverableCreated === 'function') {
        await onDeliverableCreated();
      }
    } catch (e) {
      setDelError(e.message || 'Could not create deliverable');
    }
  };

  return (
    <div className="rounded-lg border border-border/60 bg-background/30">
      <div className="p-3 flex items-center gap-3">
        <Checkbox checked={selected} onCheckedChange={onToggle} />
        <button type="button"
                className="flex-1 text-left flex items-center gap-2 min-w-0"
                onClick={() => setOpen((v) => !v)}>
          <span className="font-medium truncate">{project.name}</span>
          <Badge variant="outline" className="text-[10px] font-mono shrink-0">{project.code}</Badge>
          {open
            ? <span className="text-xs text-muted-foreground ml-auto">hide deliverables</span>
            : <span className="text-xs text-muted-foreground ml-auto">show deliverables</span>}
        </button>
      </div>

      {/* S3 prefix preview */}
      {selected && clientCode && (
        <div className="px-3 pb-2 text-[10px] text-muted-foreground font-mono">
          s3://&lt;bucket&gt;/{clientCode}/{industryCode}/{project.code}/&lt;deliverable&gt;/&lt;DDMMYYYY&gt;/
        </div>
      )}

      {open && (
        <div className="border-t border-border/60 p-3 space-y-2 bg-background/40">
          {isLoading && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Loader2 className="w-3 h-3 animate-spin" /> Loading deliverables…
            </div>
          )}
          {(dels || []).length === 0 && !isLoading && (
            <div className="text-xs text-muted-foreground italic">
              No deliverables yet for this project.
            </div>
          )}
          {(dels || []).map((d) => (
            <div key={d.id} className="flex items-center gap-2 text-sm">
              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
              <span>{d.name}</span>
              <Badge variant="outline" className="text-[10px] font-mono">{d.code}</Badge>
            </div>
          ))}

          {/* Inline new-deliverable */}
          {showAddDel ? (
            <div className="rounded-md border border-dashed border-muted-foreground/30 p-2 mt-2 space-y-2">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                <div className="md:col-span-2 space-y-1">
                  <Label className="text-xs">Deliverable name</Label>
                  <Input
                    placeholder="e.g. Encroachment Analysis"
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Code (auto = next DEL_NNN)</Label>
                  <Input
                    placeholder="DEL_002"
                    value={newCode}
                    onChange={(e) => setNewCode(e.target.value.toUpperCase())}
                    className="font-mono text-sm"
                  />
                </div>
              </div>
              {delError && (
                <div className="text-xs text-destructive flex items-center gap-1.5">
                  <AlertTriangle className="w-3.5 h-3.5" /> {delError}
                </div>
              )}
              <div className="flex justify-end gap-2">
                <Button type="button" size="sm" variant="ghost"
                        onClick={() => { setShowAddDel(false); setDelError(''); }}>
                  Cancel
                </Button>
                <Button type="button" size="sm" onClick={submitNewDeliverable}
                        disabled={createDeliverable.isPending}>
                  {createDeliverable.isPending
                    ? <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" />
                    : <Plus className="w-3.5 h-3.5 mr-2" />}
                  Create deliverable
                </Button>
              </div>
            </div>
          ) : (
            <Button type="button" variant="outline" size="sm" className="mt-1"
                    onClick={() => setShowAddDel(true)}>
              <Plus className="w-3.5 h-3.5 mr-2" /> Add deliverable
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
